import { useRef, useState } from "react";
import { Chip, FormControlLabel, Grid, Switch } from "@mui/material";
import { Button } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";

import Accordion from "components/common/accordion/Accordion";
import useInsuranceInformation from "./useInsuranceInformation";
import InsuranceInformationItem from "./InsuranceInformationItem";
import InsuranceInformationForm from "./form/InsuranceInformationForm";
import { showToast } from "utils/toast";
import { useGetInsurancesQuery } from "app/store/insurance/api/insuranceApi";

const InsuranceInformationDetail = ({
  type,
  title,
  allowEdit = true,
  isBenchmarkData = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editActive, setEditActive] = useState(null);
  const formikRef = useRef(null);

  const { data: insuranceList } = useGetInsurancesQuery();

  const {
    handleSubmitInsurance,
    profile,
    information,
    originalInsurance,
    benchmarkInsurances,
    originalTertiaryInsurance,
    originalSecondaryInsurance,
  } = useInsuranceInformation();

  const insuranceData = isBenchmarkData
    ? benchmarkInsurances?.[type]
    : type === "insurance"
      ? originalInsurance
      : type === "secondaryInsurance"
        ? originalSecondaryInsurance
        : originalTertiaryInsurance;

  const handleSubmit = async (insuranceType, values) => {
    try {
      setIsLoading(true);
      let newValues = { ...values };

      // converting null value to empty string
      Object.entries(newValues)?.forEach(([key, value]) => {
        if (key !== "active") {
          newValues[key] = value || "";
        }
      });

      const response = await handleSubmitInsurance(insuranceType, newValues);

      if (response?.error) {
        showToast({ message: response?.error?.message, type: "error" });
      } else {
        setIsEditMode(false);
        showToast({ message: response?.data?.message });
      }
    } catch (error) {
      showToast({ message: error?.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Accordion
      title={
        <Grid container className="d-flex align-items-center">
          <Grid item xs={11} className="d-flex align-items-center">
            <div className="device-info__device-title">{title}</div>
            {isEditMode && (
              <div
                className="ml-2"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <FormControlLabel
                  label="Active"
                  labelPlacement="start"
                  control={
                    <Switch
                      name="active"
                      color="secondary"
                      checked={Boolean(editActive)}
                      disabled={editActive === null}
                      onChange={(_event, checked) => {
                        formikRef.current?.setFieldValue("active", checked);
                      }}
                    />
                  }
                />
              </div>
            )}
            {!isEditMode && (
              <div className="ml-2">
                <Chip
                  color={insuranceData?.active ? "success" : "default"}
                  label={
                    <span className="d-flex align-items-center">
                      {insuranceData?.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  }
                />
              </div>
            )}
          </Grid>
          {allowEdit && !isEditMode && (
            <Grid item xs={1} style={{ textAlign: "right" }}>
              <Button
                variant="text"
                color="secondary"
                className="device-edit-button mr-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditMode(true);
                }}
              >
                <EditIcon
                  className="patient-edit-icon billing-cycle-edit-icon"
                  data-action="edit-insurance-information-icon"
                />
                Edit
              </Button>
            </Grid>
          )}
        </Grid>
      }
      isBordered={false}
      hasPersistentDivider
      isDefaultExpanded={
        type === "insurance" ||
        (isBenchmarkData && insuranceData?.active) ||
        isEditMode
      }
    >
      <div className="d-flex flex-column">
        {isEditMode ? (
          <InsuranceInformationForm
            type={type}
            insurance={insuranceData}
            insuranceList={insuranceList}
            setIsEditMode={setIsEditMode}
            formikRef={formikRef}
            onActiveChange={setEditActive}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            profile={profile}
            information={information}
          />
        ) : (
          <InsuranceInformationItem insurance={insuranceData} />
        )}
        {/* {isEditMode && (
          <div
            style={{ textAlign: "right", gap: "10px" }}
            className="d-flex flex-end"
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                handleCancel(type);
                setIsEditMode(false);
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <SubmitButton
              variant="contained"
              color="secondary"
              handleClick={() => handleSubmit(type)}
              isLoading={isLoading}
            />
          </div>
        )} */}
      </div>
    </Accordion>
  );
};

export default InsuranceInformationDetail;
